'use client';

import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Kbd } from '../Kbd/Kbd';

export type Platform = 'mac' | 'pc';

export interface KeyboardShortcutProps {
  /**
   * Shortcut string. Use `+` as delimiter, `Mod` for the primary modifier
   * (⌘ on Mac, Ctrl on PC), `Shift`, `Alt`, `Esc`, or any bare key letter.
   *
   * @example "Mod+K", "Shift+?", "Esc"
   */
  shortcut: string;
  /** Platform determines how modifiers are labelled. Defaults to auto-detect. */
  platform?: Platform;
  className?: string;
}

export interface UseKeyboardShortcutOptions {
  /** Disable the binding without removing the hook. Default true. */
  enabled?: boolean;
}

/** Split a shortcut string into its constituent key tokens. */
export function parseShortcut(shortcut: string): string[] {
  return shortcut.split('+');
}

const MAC_MAP: Record<string, string> = {
  Mod: '⌘',
  Alt: '⌥',
  Shift: '⇧',
  Ctrl: '⌃',
};

const PC_MAP: Record<string, string> = {
  Mod: 'Ctrl',
};

/** Format a single key token for display on the given platform. */
export function formatKey(key: string, platform: Platform): string {
  const map = platform === 'mac' ? MAC_MAP : PC_MAP;
  return map[key] ?? key;
}

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'pc';
  return /Mac|iPhone|iPod|iPad/.test(navigator.platform) ? 'mac' : 'pc';
}

const Root = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const Sep = styled.span`
  font-size: 10px;
  color: ${(p) => p.theme.colors.neutral.inkFaint};
  margin: 0 1px;
  user-select: none;
`;

/**
 * Renders a keyboard shortcut as a sequence of styled `Kbd` key chips.
 * Automatically translates modifier names for the detected platform.
 *
 * @example
 * <KeyboardShortcut shortcut="Mod+K" />  // renders ⌘ K on Mac, Ctrl K on PC
 */
export function KeyboardShortcut({ shortcut, platform, className }: KeyboardShortcutProps) {
  const plat = platform ?? detectPlatform();
  const keys = parseShortcut(shortcut);

  return (
    <Root className={className}>
      {keys.map((key, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Sep>+</Sep>}
          <Kbd>{formatKey(key, plat)}</Kbd>
        </React.Fragment>
      ))}
    </Root>
  );
}

/**
 * Binds a keyboard shortcut to a handler. Fires on `document` keydown.
 * Supports `Mod` (Meta on Mac, Ctrl on PC), `Shift`, `Alt`.
 *
 * @example
 * useKeyboardShortcut('Mod+k', () => openCommandPalette());
 */
export function useKeyboardShortcut(
  shortcut: string,
  handler: () => void,
  options: UseKeyboardShortcutOptions = {},
): void {
  const { enabled = true } = options;
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;

    const keys = parseShortcut(shortcut);
    const plat = detectPlatform();

    function listener(e: KeyboardEvent) {
      let remaining = [...keys];

      // Process modifiers first
      remaining = remaining.filter((k) => {
        if (k === 'Mod') {
          return plat === 'mac' ? !e.metaKey : !e.ctrlKey;
        }
        if (k === 'Shift') return !e.shiftKey;
        if (k === 'Alt') return !e.altKey;
        if (k === 'Ctrl') return !e.ctrlKey;
        if (k === 'Meta') return !e.metaKey;
        return true;
      });

      // After filtering modifiers, one bare key should remain
      if (remaining.length !== 1) return;
      if (e.key.toLowerCase() !== remaining[0].toLowerCase()) return;

      // Confirm modifiers were held
      const wantsShift = keys.includes('Shift');
      const wantsMod = keys.includes('Mod');
      const wantsAlt = keys.includes('Alt');

      if (wantsMod && (plat === 'mac' ? !e.metaKey : !e.ctrlKey)) return;
      if (wantsShift && !e.shiftKey) return;
      if (wantsAlt && !e.altKey) return;

      handlerRef.current();
    }

    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [shortcut, enabled]);
}
